/**
 * Seed-Daten für Test-Umgebung
 *
 * NEUE STRUKTUR (Februar 2024):
 * - Sterne-Bewertung (0-5) statt Schulnoten
 * - 3 Bewertungsebenen (Agentur, Team, Chemie)
 *
 * WICHTIG: Diese Daten sind NUR für die Test-Umgebung.
 * Die Produktions-Datenbank startet leer.
 */

import type { Agency, Rating, RatingScores, RatingOccasion } from '@/types';
import { getAgencyFaviconUrl } from '@/lib/avatar';

// ============================================================================
// AGENCY HELPERS
// ============================================================================

function createAgency(
  id: string,
  name: string,
  type: Agency['type'],
  city: string,
  website: string
): Agency {
  return {
    id,
    partitionKey: type,
    name,
    type,
    city,
    website,
    logo: getAgencyFaviconUrl(website) || undefined,
    createdAt: '2024-01-01T00:00:00Z',
    updatedAt: '2024-01-01T00:00:00Z',
    createdBy: 'system',
  };
}

// ============================================================================
// SEED AGENCIES (Minimal Set)
// ============================================================================

export const SEED_AGENCIES: Agency[] = [
  // Kreativ-Agenturen
  createAgency('jung-von-matt', 'Jung von Matt', 'Kreativ', 'Hamburg', 'https://jvm.com'),
  createAgency('serviceplan', 'Serviceplan', 'Kreativ', 'München', 'https://serviceplan.com'),
  createAgency('heimat', 'Heimat Berlin', 'Kreativ', 'Berlin', 'https://heimat-berlin.com'),

  // Digital-Agenturen
  createAgency('sinner-schrader', 'SinnerSchrader', 'Digital', 'Hamburg', 'https://sinnerschrader.com'),
  createAgency('accenture-song', 'Accenture Song', 'Digital', 'München', 'https://accenture.com/song'),

  // Media-Agenturen
  createAgency('mediacom', 'MediaCom', 'Media', 'Düsseldorf', 'https://mediacom.com'),

  // PR-Agenturen
  createAgency('edelman', 'Edelman Germany', 'PR', 'Frankfurt', 'https://edelman.de'),
];

// ============================================================================
// RATING HELPERS
// ============================================================================

/**
 * Erstellt ein vollständiges Score-Objekt im neuen Format
 */
function createScores(
  agency: { lead: number; strategic: number; quality: number; market: number; intl: number; task: number },
  team: { strategy: number; creative: number; targetedCreativity: number; account: number; consulting: number; dynamics: number; projectMgmt: number; discussion: number; proactive: number },
  chemistry: { fit: number; passion: number; comm: number; chem: number; reliable: number; feedback: number }
): RatingScores {
  return {
    agency: {
      main: {
        leadAgencySuitability: agency.lead as 0 | 1 | 2 | 3 | 4 | 5,
        strategicCompetence: agency.strategic as 0 | 1 | 2 | 3 | 4 | 5,
        workQuality: agency.quality as 0 | 1 | 2 | 3 | 4 | 5,
        marketUnderstanding: agency.market as 0 | 1 | 2 | 3 | 4 | 5,
        internationalExperience: agency.intl as 0 | 1 | 2 | 3 | 4 | 5,
        taskFit: agency.task as 0 | 1 | 2 | 3 | 4 | 5,
      },
    },
    team: {
      main: {
        strategyCompetence: team.strategy as 0 | 1 | 2 | 3 | 4 | 5,
        creativeCompetence: team.creative as 0 | 1 | 2 | 3 | 4 | 5,
        targetedCreativity: team.targetedCreativity as 0 | 1 | 2 | 3 | 4 | 5,
        accountCompetence: team.account as 0 | 1 | 2 | 3 | 4 | 5,
        consultingCompetence: team.consulting as 0 | 1 | 2 | 3 | 4 | 5,
        teamDynamics: team.dynamics as 0 | 1 | 2 | 3 | 4 | 5,
        projectManagement: team.projectMgmt as 0 | 1 | 2 | 3 | 4 | 5,
        discussionQuality: team.discussion as 0 | 1 | 2 | 3 | 4 | 5,
        proactivity: team.proactive as 0 | 1 | 2 | 3 | 4 | 5,
      },
    },
    chemistry: {
      main: {
        teamFit: chemistry.fit as 0 | 1 | 2 | 3 | 4 | 5,
        passionEnthusiasm: chemistry.passion as 0 | 1 | 2 | 3 | 4 | 5,
        communicationStyle: chemistry.comm as 0 | 1 | 2 | 3 | 4 | 5,
        chemistry: chemistry.chem as 0 | 1 | 2 | 3 | 4 | 5,
        reliability: chemistry.reliable as 0 | 1 | 2 | 3 | 4 | 5,
        feedbackOpenness: chemistry.feedback as 0 | 1 | 2 | 3 | 4 | 5,
      },
    },
  };
}

function createRating(
  id: string,
  agencyId: string,
  scores: RatingScores,
  createdAt: string,
  occasion?: RatingOccasion,
  overallNotes?: string
): Rating {
  return {
    id,
    partitionKey: agencyId,
    agencyId,
    userId: 'seed-user',
    ratedBy: {
      id: 'seed-user',
      displayName: 'Demo User',
      avatar: undefined,
    },
    persona: 'cherrypicker',
    scores,
    notes: {},
    createdAt,
    period: createdAt.slice(0, 7),
    source: 'manual',
    occasion,
    overallNotes,
  };
}

// ============================================================================
// SEED RATINGS
// ============================================================================

export const SEED_RATINGS: Rating[] = [
  // Jung von Matt - Exzellent
  createRating(
    'rating-jvm-1',
    'jung-von-matt',
    createScores(
      { lead: 5, strategic: 5, quality: 5, market: 5, intl: 4, task: 5 },
      { strategy: 5, creative: 5, targetedCreativity: 5, account: 4, consulting: 4, dynamics: 5, projectMgmt: 5, discussion: 5, proactive: 5 },
      { fit: 5, passion: 5, comm: 5, chem: 5, reliable: 4, feedback: 5 }
    ),
    '2024-01-15T10:00:00Z',
    'chemistry_meeting',
    'Hervorragender Eindruck beim Chemistry Meeting. Team sehr engagiert.'
  ),

  // Serviceplan - Gut
  createRating(
    'rating-sp-1',
    'serviceplan',
    createScores(
      { lead: 4, strategic: 4, quality: 4, market: 4, intl: 5, task: 4 },
      { strategy: 4, creative: 4, targetedCreativity: 4, account: 4, consulting: 4, dynamics: 4, projectMgmt: 4, discussion: 4, proactive: 3 },
      { fit: 4, passion: 4, comm: 4, chem: 4, reliable: 5, feedback: 4 }
    ),
    '2024-01-20T14:00:00Z',
    'pitch',
    'Solider Pitch, gute internationale Aufstellung.'
  ),

  // Heimat - Sehr gut
  createRating(
    'rating-heimat-1',
    'heimat',
    createScores(
      { lead: 4, strategic: 5, quality: 4, market: 4, intl: 3, task: 5 },
      { strategy: 5, creative: 5, targetedCreativity: 5, account: 4, consulting: 4, dynamics: 4, projectMgmt: 4, discussion: 5, proactive: 4 },
      { fit: 5, passion: 5, comm: 5, chem: 4, reliable: 4, feedback: 5 }
    ),
    '2024-02-01T09:00:00Z',
    'chemistry_meeting',
    'Kreativ stark, Chemie stimmt. National fokussiert.'
  ),

  // SinnerSchrader - Gut
  createRating(
    'rating-ss-1',
    'sinner-schrader',
    createScores(
      { lead: 3, strategic: 5, quality: 4, market: 4, intl: 4, task: 4 },
      { strategy: 4, creative: 4, targetedCreativity: 4, account: 3, consulting: 3, dynamics: 4, projectMgmt: 4, discussion: 4, proactive: 4 },
      { fit: 4, passion: 4, comm: 4, chem: 4, reliable: 4, feedback: 4 }
    ),
    '2024-02-05T11:00:00Z',
    'longlist_workshop',
    'Starke digitale Kompetenz, Account-Seite ausbaufähig.'
  ),

  // Accenture Song - Befriedigend bis Gut
  createRating(
    'rating-as-1',
    'accenture-song',
    createScores(
      { lead: 4, strategic: 4, quality: 3, market: 4, intl: 5, task: 3 },
      { strategy: 4, creative: 3, targetedCreativity: 3, account: 4, consulting: 3, dynamics: 3, projectMgmt: 3, discussion: 3, proactive: 3 },
      { fit: 3, passion: 3, comm: 4, chem: 3, reliable: 4, feedback: 3 }
    ),
    '2024-02-10T15:00:00Z',
    'pitch',
    'Große Ressourcen, aber Kreativität und Chemie könnten besser sein.'
  ),

  // MediaCom - Gut
  createRating(
    'rating-mc-1',
    'mediacom',
    createScores(
      { lead: 4, strategic: 4, quality: 4, market: 5, intl: 5, task: 4 },
      { strategy: 4, creative: 3, targetedCreativity: 3, account: 4, consulting: 4, dynamics: 4, projectMgmt: 4, discussion: 4, proactive: 4 },
      { fit: 4, passion: 4, comm: 4, chem: 4, reliable: 5, feedback: 4 }
    ),
    '2024-02-15T10:00:00Z',
    'other',
    'Starke Media-Expertise, zuverlässiger Partner.'
  ),

  // Edelman - Gut
  createRating(
    'rating-ed-1',
    'edelman',
    createScores(
      { lead: 4, strategic: 4, quality: 4, market: 5, intl: 5, task: 4 },
      { strategy: 5, creative: 3, targetedCreativity: 3, account: 4, consulting: 4, dynamics: 4, projectMgmt: 4, discussion: 4, proactive: 4 },
      { fit: 4, passion: 4, comm: 5, chem: 4, reliable: 4, feedback: 4 }
    ),
    '2024-02-20T14:00:00Z',
    'chemistry_meeting',
    'Exzellente PR-Strategie, Kommunikation top.'
  ),
];
